import { useCallback, useState } from 'react';
import { ExpandMore } from '@mui/icons-material';
import {
  Box,
  Collapse,
  Divider,
  InputAdornment,
  MenuItem,
  Select,
  type SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';

import type { EligibilityCheckResponse, EligibilityFinancials } from '../../types';
import { getBenefitExplainer, getFinancials } from '../../types';

/** Fields on EligibilityCheckResponse that the override endpoint accepts */
export type CoverageField =
  | 'coverage_status'
  | 'copayment'
  | 'coinsurance'
  | 'deductible'
  | 'remaining_deductible'
  | 'oop_max'
  | 'remaining_oop_max'
  | 'member_obligation';

type NumericCoverageField = Exclude<CoverageField, 'coverage_status'>;

export type CoverageOverrides = {
  coverage_status?: string | null;
} & Partial<Record<NumericCoverageField, number | null>>;

interface CoverageDetailsGridProps {
  data: EligibilityCheckResponse | undefined;
  isOverrideMode?: boolean;
  overrides?: CoverageOverrides;
  onFieldChange?: (field: CoverageField, value: number | string | null) => void;
  onBlur?: () => void;
}

function formatCurrency(value: number | null | undefined): string {
  if (value == null) return '—';
  return '$' + value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatPercent(value: number | null | undefined): string {
  if (value == null) return '—';
  return `${value}%`;
}

function formatStatus(value: string | null | undefined): string {
  if (!value) return '—';
  return value.charAt(0).toUpperCase() + value.slice(1);
}

// ── Read-only row ─────────────────────────────────────────────────────

interface DetailRowProps {
  label: string;
  value: string;
  secondaryLabel?: string;
  secondaryValue?: string;
}

const emptyFieldSx = {
  border: '1px dashed',
  borderColor: 'action.disabled',
  borderRadius: 0.5,
  px: 1,
  py: 0.25,
};

function DetailCell({ label, value }: { label: string; value: string }) {
  const isEmpty = value === '—';
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: 1,
        ...(isEmpty ? emptyFieldSx : {}),
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        fontWeight={isEmpty ? 400 : 500}
        color={isEmpty ? 'text.disabled' : 'text.primary'}
      >
        {value}
      </Typography>
    </Box>
  );
}

function DetailRow({ label, value, secondaryLabel, secondaryValue }: DetailRowProps) {
  if (!secondaryLabel) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
          {label}
        </Typography>
        <Typography variant="body2" fontWeight={value === '—' ? 400 : 500} color={value === '—' ? 'text.disabled' : 'text.primary'}>
          {value}
        </Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
      <DetailCell label={label} value={value} />
      <DetailCell label={secondaryLabel} value={secondaryValue ?? '—'} />
    </Box>
  );
}

// ── Editable row (override mode) ──────────────────────────────────────

interface EditableRowProps {
  label: string;
  field: CoverageField;
  value: number | null | undefined;
  adornment?: string;
  secondaryLabel?: string;
  secondaryField?: CoverageField;
  secondaryValue?: number | null | undefined;
  secondaryAdornment?: string;
  onFieldChange: (field: CoverageField, value: string | number | null) => void;
  onBlur?: () => void;
}

function EditableRow({
  label,
  field,
  value,
  adornment = '$',
  secondaryLabel,
  secondaryField,
  secondaryValue,
  secondaryAdornment = '$',
  onFieldChange,
  onBlur,
}: EditableRowProps) {
  const handleChange = useCallback(
    (f: CoverageField) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      if (raw === '') {
        onFieldChange(f, null);
      } else {
        const num = parseFloat(raw);
        if (!isNaN(num)) onFieldChange(f, num);
      }
    },
    [onFieldChange],
  );

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: secondaryLabel ? '1fr 1fr' : '1fr', gap: 2 }}>
      <TextField
        label={label}
        value={value ?? ''}
        onChange={handleChange(field)}
        onBlur={onBlur}
        size="small"
        type="number"
        InputProps={{
          startAdornment: adornment ? (
            <InputAdornment position="start">{adornment}</InputAdornment>
          ) : undefined,
        }}
      />
      {secondaryLabel && secondaryField && (
        <TextField
          label={secondaryLabel}
          value={secondaryValue ?? ''}
          onChange={handleChange(secondaryField)}
          onBlur={onBlur}
          size="small"
          type="number"
          InputProps={{
            startAdornment: secondaryAdornment ? (
              <InputAdornment position="start">{secondaryAdornment}</InputAdornment>
            ) : undefined,
          }}
        />
      )}
    </Box>
  );
}

// ── Main component ────────────────────────────────────────────────────

const COVERAGE_STATUS_OPTIONS = ['active', 'inactive', 'terminated'];

export function CoverageDetailsGrid({
  data,
  isOverrideMode,
  overrides = {},
  onFieldChange,
  onBlur,
}: CoverageDetailsGridProps) {
  const [moreInfoOpen, setMoreInfoOpen] = useState(false);

  if (!data) return null;

  const fin = getFinancials(data);

  // Helper: get the effective value for a numeric field (override wins, else parsed financials)
  const num = (field: NumericCoverageField): number | null | undefined =>
    overrides[field] !== undefined ? overrides[field] : (fin[field] as number | null | undefined);

  const effectiveCoverageStatus = overrides.coverage_status ?? data.coverage_status;

  const hasAnyData =
    num('copayment') != null ||
    num('coinsurance') != null ||
    num('deductible') != null ||
    num('oop_max') != null ||
    num('member_obligation') != null;

  const hasSecondaryData =
    num('copayment') != null ||
    num('coinsurance') != null ||
    num('deductible') != null ||
    num('oop_max') != null ||
    data.plan_status != null;

  const handleCoverageStatusChange = (event: SelectChangeEvent) => {
    onFieldChange?.('coverage_status', event.target.value);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Divider sx={{ mb: 2 }} />

      <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
        Coverage Details
      </Typography>

      {!hasAnyData && !isOverrideMode ? (
        <Typography variant="body2" color="text.secondary">
          No coverage details available
        </Typography>
      ) : isOverrideMode && onFieldChange ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* PRIMARY: Coverage Status + Member Obligation */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                Coverage Status
              </Typography>
              <Select
                value={effectiveCoverageStatus || ''}
                onChange={handleCoverageStatusChange}
                size="small"
                fullWidth
              >
                {COVERAGE_STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt.charAt(0).toUpperCase() + opt.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <Box>
              <TextField
                label="Est. Patient Responsibility"
                value={num('member_obligation') ?? ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const raw = e.target.value;
                  if (raw === '') onFieldChange('member_obligation', null);
                  else {
                    const n = parseFloat(raw);
                    if (!isNaN(n)) onFieldChange('member_obligation', n);
                  }
                }}
                onBlur={onBlur}
                size="small"
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </Box>
          </Box>
          {fin.benefit_structure && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                {fin.benefit_structure}
              </Typography>
              {(() => {
                const explainer = getBenefitExplainer(fin);
                return explainer ? (
                  <Typography variant="caption" sx={{ display: 'block', fontStyle: 'italic', color: 'text.secondary', mt: 0.25 }}>
                    {explainer}
                  </Typography>
                ) : null;
              })()}
            </Box>
          )}

          {/* SECONDARY: More Info — collapsible */}
          <Box
            component="button"
            onClick={() => setMoreInfoOpen(prev => !prev)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              p: 0,
              color: 'primary.main',
              fontSize: '0.8125rem',
              fontWeight: 500,
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            More info
            <ExpandMore
              sx={{
                fontSize: 16,
                transition: 'transform 0.2s',
                transform: moreInfoOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            />
          </Box>
          <Collapse in={moreInfoOpen}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">Plan Status</Typography>
                <Typography variant="body2" fontWeight={500}>{formatStatus(data.plan_status)}</Typography>
              </Box>
              <EditableRow
                label="Copayment"
                field="copayment"
                value={num('copayment')}
                secondaryLabel="Coinsurance"
                secondaryField="coinsurance"
                secondaryValue={num('coinsurance')}
                secondaryAdornment="%"
                onFieldChange={onFieldChange}
                onBlur={onBlur}
              />
              <EditableRow
                label="Deductible"
                field="deductible"
                value={num('deductible')}
                secondaryLabel="Remaining"
                secondaryField="remaining_deductible"
                secondaryValue={num('remaining_deductible')}
                onFieldChange={onFieldChange}
                onBlur={onBlur}
              />
              <EditableRow
                label="OOP Max"
                field="oop_max"
                value={num('oop_max')}
                secondaryLabel="Remaining"
                secondaryField="remaining_oop_max"
                secondaryValue={num('remaining_oop_max')}
                onFieldChange={onFieldChange}
                onBlur={onBlur}
              />
            </Box>
          </Collapse>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {/* PRIMARY: Coverage Status + Member Obligation */}
          <DetailRow
            label="Coverage Status"
            value={formatStatus(effectiveCoverageStatus)}
            secondaryLabel="Est. Responsibility"
            secondaryValue={formatCurrency(num('member_obligation'))}
          />
          {fin.benefit_structure && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                {fin.benefit_structure}
              </Typography>
              {(() => {
                const explainer = getBenefitExplainer(fin);
                return explainer ? (
                  <Typography variant="caption" sx={{ display: 'block', fontStyle: 'italic', color: 'text.secondary', mt: 0.25 }}>
                    {explainer}
                  </Typography>
                ) : null;
              })()}
            </Box>
          )}

          {/* SECONDARY: More Info — collapsible */}
          {hasSecondaryData && (
            <>
              <Box
                component="button"
                onClick={() => setMoreInfoOpen(prev => !prev)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  p: 0,
                  color: 'primary.main',
                  fontSize: '0.8125rem',
                  fontWeight: 500,
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                More info
                <ExpandMore
                  sx={{
                    fontSize: 16,
                    transition: 'transform 0.2s',
                    transform: moreInfoOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                />
              </Box>
              <Collapse in={moreInfoOpen}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 0.5 }}>
                  {data.plan_status && (
                    <DetailRow label="Plan Status" value={formatStatus(data.plan_status)} />
                  )}
                  {(num('copayment') != null || num('coinsurance') != null) && (
                    <DetailRow
                      label="Copayment"
                      value={formatCurrency(num('copayment'))}
                      {...(num('coinsurance') != null
                        ? { secondaryLabel: 'Coinsurance', secondaryValue: formatPercent(num('coinsurance')) }
                        : {})}
                    />
                  )}
                  {(num('deductible') != null || num('remaining_deductible') != null) && (
                    <DetailRow
                      label="Deductible"
                      value={formatCurrency(num('deductible'))}
                      {...(num('remaining_deductible') != null
                        ? { secondaryLabel: 'Remaining', secondaryValue: formatCurrency(num('remaining_deductible')) }
                        : {})}
                    />
                  )}
                  {(num('oop_max') != null || num('remaining_oop_max') != null) && (
                    <DetailRow
                      label="OOP Max"
                      value={formatCurrency(num('oop_max'))}
                      {...(num('remaining_oop_max') != null
                        ? { secondaryLabel: 'Remaining', secondaryValue: formatCurrency(num('remaining_oop_max')) }
                        : {})}
                    />
                  )}
                </Box>
              </Collapse>
            </>
          )}
        </Box>
      )}
    </Box>
  );
}
